import _ from 'lodash'
import {MutationTree} from 'vuex'
import {UWRouterState} from '@/modules/app-router/store/index'
import AppRelease from '@/modules/app-router/model/AppRelease'
import App from '@/modules/app-router/model/App';
import UserGroup from '@/modules/user/model/UserGroup';
import Api from '@/modules/app-router/model/Api';
import ApiVersion from '@/modules/app-router/model/ApiVersion';
import ReleaseChange from '@/modules/app-router/model/ReleaseChange';
import GroupReleaseAssignation from '@/modules/app-router/model/GroupReleaseAssignation';
import {GetReleaseLogInput, ReleaseLogsList} from '@/modules/app-router/api';
import ReleaseLog from '@/modules/app-router/model/ReleaseLog';
import SQLConnectionConfig from '@/modules/app-router/model/SQLConnectionConfig';

export enum MutationTypes {
    // Mutaciones para aplicaciones del enrutador
    SetAppList = 'SET_APP_LIST',
    SetCurrentApp = 'SET_CURRENT_APP',
    // Mutaciones para publicaciones de una app
    SetAppReleasesList = 'SET_APP_RELEASES_LIST',
    SetCurrentAppRelease = 'SET_CURRENT_APP_RELEASE',
    UpdateReleaseField = 'UPDATE_APP_RELEASE_FIELD',
    AddAppRelease = 'ADD_APP_RELEASE',
    RemoveAppRelease = 'REMOVE_APP_RELEASE',
    RemoveReleaseUserGroups = 'REMOVE_RELEASE_USER_GROUPS',
    AddReleaseUserGroups = 'ADD_RELEASE_USER_GROUPS',
    ToggleDisplayDisabledReleases = 'TOGGLE_DISPLAY_DISABLED_RELEASES',
    ToggleDisplayDefaultRelease = 'TOGGLE_DISPLAY_DEFAULT_RELEASE',

    // Mutaciones para las APIs
    SetApiList = 'SET_API_LIST',
    ToggleApiListFetched = 'TOGGLE_API_LIST_FETCHED',
    SetCurrentApi = 'SET_CURRENT_API',

    // Mutaciones para versiones de la API
    SetCurrentApiVersion = 'SET_CURRENT_API_VERSION',
    AddApiVersion = 'ADD_API_VERSION',
    UpdateCurrentApiVersionField = 'UPDATE_CURRENT_API_VERSION_FIELD',
    RemoveApiVersion = 'REMOVE_API_VERSION',
    SetFilterReleasesByApiVersion = 'SET_FILTER_RELEASES_BY_API_VERSION',
    ResetFilterReleasesByApiVersion = 'RESET_FILTER_RELEASES_BY_API_VERSION',
    ToggleApiVersionDetail = 'TOGGLE_API_VERSION_DETAIL',

    // Mutaciones para cambios de distribuciones
    SetCurrentChange = 'SET_CURRENT_CHANGE',
    UpdateChangeField = 'UPDATE_CHANGE_FIELD',
    AddChange = 'ADD_CHANGE',
    DeleteChange = 'DELETE_CHANGE',

    // Mutaciones para las asignaciones de distribuciones a grupos de usuarios
    SetGroupReleaseAssignations = 'SET_GROUP_RELEASE_ASSIGNATIONS',
    UpdateGroupReleaseAssignation = 'UPDATE_GROUP_RELEASE_ASSIGNATION',
    SetGroupReleaseAssignation = 'SET_GROUP_RELEASE_ASSIGNATION',

    // Mutaciones para registros de eventos
    SetReleaseLogsList = 'SET_RELEASE_LOGS_LIST',
    SetReleaseLogsInput = 'SET_RELEASE_LOGS_INPUT',
    UpdateReleaseLogsInputField = 'UPDATE_RELEASE_LOGS_INPUT_FIELD',
    ResetReleaseLogsInput = 'RESET_RELEASE_LOGS_INPUT',
    SetCurrentLog = 'SET_CURRENT_LOG',

    SetSQLConnections = 'SET_SQL_CONNECTIONS',
    SetCurrentSQLConnection = 'SET_CURRENT_SQL_CONNECTION',
}

const mutations: MutationTree<UWRouterState> = {
    /**
     * Establece la lista actual de aplicaciones registrada en el enrutador
     *
     * @param state
     * @param apps
     */
    [MutationTypes.SetAppList]: (state, apps: App[]) => state.apps = apps,

    /**
     * Establece la aplicación actualmente seleccionada por el usuario
     *
     * @param state
     * @param app
     */
    [MutationTypes.SetCurrentApp]: (state, app: App) => state.app = app,
    /**
     * Establece la lista de publicaciones de la app de UnigestWeb
     *
     * @param state
     * @param releases
     */
    [MutationTypes.SetAppReleasesList]: (state, releases: AppRelease[]) => state.releases = releases,

    /**
     * Establece la distribución de la app actualmente seleccionada por el usuario
     *
     * @param state
     * @param release
     */
    [MutationTypes.SetCurrentAppRelease]: (state, release: AppRelease) => state.release = release,

    /**
     * Actualiza el valor de un campo de la distribución actualmente en edición por
     * el usuario
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateReleaseField]: (state, payload: {field: string, value: any}) => {
        const { field, value } = payload
        _.set(state.release, field, value)
    },

    /**
     * Borra grupos de usuarios de la distribución actual
     *
     * @param state
     * @param payload
     */
    [MutationTypes.RemoveReleaseUserGroups]: (state, groups: UserGroup[]) => {
        const releaseGroups = state.release.userGroups
        groups.forEach(g => {
            const index = releaseGroups.findIndex(ug => ug.id === g.id)
            if (index !== -1) {
                releaseGroups.splice(index, 1)
            }
        })
    },

    /**
     * Añade una lista de grupos de usuarios a la distribución actual
     *
     * @param state
     * @param payload
     */
    [MutationTypes.AddReleaseUserGroups]: (state, groups: UserGroup[]) => {
        const releaseGroups = state.release.userGroups
        groups.forEach(g => {
            const index = releaseGroups.findIndex(ug => ug.id === g.id)
            index === -1
                ? releaseGroups.push(g)
                : releaseGroups.splice(index, 1, g)
        })
    },

    /**
     * Inserta una nueva publicación en la lista registrada en el estado de la aplicación.
     * Si ya existe una publicación con el mismo id la antigua será reemplaza por
     * la nueva.
     *
     * @param state
     * @param release
     */
    [MutationTypes.AddAppRelease]: (state, release: AppRelease) => {
        const releases = state.app.releases
        const index = state.app.releases.findIndex(r => r.id === release.id)
        index === -1
            ? releases.push(release)
            : releases.splice(index, 1, release)

        if (release.default) {
            releases.filter(r => r.id !== release.id).forEach(r => r.default = false)
        }
    },
    /**
     * Elimina una distribución de la lista
     *
     * @param state
     * @param id
     */
    [MutationTypes.RemoveAppRelease]: (state, id: number) => {
        const releases = state.app.releases
        const index = releases.findIndex(r => r.id === id)
        if (index !== -1) {
            releases.splice(index, 1)
        }
    },
    /**
     * Establece la lista de APIs mostradas al usuario
     *
     * @param state
     * @param apis
     */
    [MutationTypes.SetApiList]: (state, apis: Api[]) => state.apis = apis,

    /**
     * Establece la bandera que indica si la lista de APIs registradas ya ha sido obtenida
     *
     * @param state
     * @param fetched
     */
    [MutationTypes.ToggleApiListFetched]: (state, fetched: boolean) => state.apiListFetched = fetched,
    [MutationTypes.SetCurrentApi]: (state, api: Api) => state.currentApi = api,
    [MutationTypes.SetCurrentApiVersion]: (state, version: ApiVersion) => state.apiVersion = version,
    /**
     * Actualiza un campo de la versión de la API actualmente seleccionada
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateCurrentApiVersionField]: (state, payload: {field: string, value: any}) => {
        _.set(state.apiVersion, payload.field, payload.value)
    },

    /**
     * Inserta una nueva versión de API en la lista registrada en el estado de la aplicación.
     * Si ya existe una versión con el mismo id, la antigua será reemplaza por
     * la nueva.
     *
     * @param state
     * @param release
     */
    [MutationTypes.AddApiVersion]: (state, version: ApiVersion) => {
        const index = state.currentApi.versions.findIndex(r => r.id === version.id)
        index === -1
            ? state.currentApi.versions.push(version)
            : state.currentApi.versions.splice(index, 1, version)

    },

    /**
     * Elimina un versión de la API actualmente seleccionada
     *
     * @param state
     * @param id
     */
    [MutationTypes.RemoveApiVersion]: (state, id: number) => {
        const index = state.currentApi.versions.findIndex(v => v.id === id)
        if (index !== -1) {
            state.currentApi.versions.slice(index, 1)
        }
    },

    /**
     * Activa o desactiva la opción para mostrar las distribuciones deshabilitadas.
     *
     * @param state
     * @param display
     */
    [MutationTypes.ToggleDisplayDisabledReleases]: (state, display: boolean) => {
        state.displayDisabledReleases = display
    },

    /**
     * Activa o desactiva la opción para mostrar únicamente la distribución activa por
     * defecto en la pantalla de distribuciones.
     *
     * @param state
     * @param display
     */
    [MutationTypes.ToggleDisplayDefaultRelease]: (state, display: boolean) => {
        state.displayDefaultRelease = display
    },

    /**
     * Establece el cambio actualmente seleccionado por el usuario
     *
     * @param state
     * @param change
     */
    [MutationTypes.SetCurrentChange]: (state, change: ReleaseChange) => {
        state.currentChange = change || new ReleaseChange()
    },

    /**
     * Actualiza un campo del cambio actualmente seleccionado por el usuario
     *
     * @param state
     * @param change
     */
    [MutationTypes.UpdateChangeField]: (state, payload: { field: string, value: any }) => {
        _.set(state.currentChange, payload.field, payload.value)
    },

    /**
     * Añade un cambio a la distribución actualmente seleccionada por el
     * usuario
     *
     * @param state
     * @param change
     */
    [MutationTypes.AddChange]: (state, change: ReleaseChange) => {
        state.release.changes.push(change)
    },
    /**
     * Elimina un cambios registrado en la distribución actualmente seleccionada
     * por el usuario.
     *
     * @param state
     * @param change
     */
    [MutationTypes.DeleteChange]: (state, id: string) => {
        const index = state.release.changes.findIndex(c => c.tempId === id)
        index !== -1 && state.release.changes.splice(index, 1)
    },

    /**
     * Establece la lista de asignaciones de distribuciones a grupos de usuarios
     *
     * @param state
     * @param assignations
     */
    [MutationTypes.SetGroupReleaseAssignations]: (state, assignations: GroupReleaseAssignation[]) => {
        state.groupReleaseAssignations = assignations
    },

    /**
     * Actualiza una asignación de distribución a grupo de usuario. Si no existe ninguna insertará
     * para el grupo argumentado se insertará una nueva.
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateGroupReleaseAssignation]: (state, payload: {group: UserGroup, release: AppRelease}) => {
        const { group, release } = payload
        let assignation = state
            .groupReleaseAssignations
            .find(a => a.group.id === group.id)

        // Ya existe una asignación para el grupo
        if (assignation) {
            assignation.release = release
        }

        assignation = new GroupReleaseAssignation(release, group)
        state.groupReleaseAssignations.push(assignation)
    },
    /**
     * Establece la versión que será utilizada para filtrar la distribuciones
     *
     * @param state
     * @param assignations
     */
    [MutationTypes.SetFilterReleasesByApiVersion]: (state, api: ApiVersion) => {
        state.filterReleasesByApiVersion = api
    },
    /**
     * Resetea el filtro actual de distribuciones por versión de API
     *
     * @param state
     * @param assignations
     */
    [MutationTypes.ResetFilterReleasesByApiVersion]: (state) => {
        state.filterReleasesByApiVersion = new ApiVersion()
    },

    /**
     * Activa o desactiva la pantalla que muestra la información de una versión de una API
     */
    [MutationTypes.ToggleApiVersionDetail]: (state, display: boolean) => {
        state.apiVersionDetail = display
    },

    [MutationTypes.SetReleaseLogsInput]: (state, payload: GetReleaseLogInput) => state.getReleaseLogsInput = payload,
    [MutationTypes.SetReleaseLogsList]: (state, list: ReleaseLogsList) => state.releaseLogs = list,
    /**
     * Actualiza uno de los campos del objecto GetReleaseLogsInput
     *
     * @param state
     * @param payload
     */
    [MutationTypes.UpdateReleaseLogsInputField]: (state, payload: {field: string, value: any})  => {
        _.set(state.getReleaseLogsInput, payload.field, payload.value)
    },
    /**
     * Limpia cualquier filtro de búsqueda registrado en el input
     *
     * @param state
     */
    [MutationTypes.ResetReleaseLogsInput]: (state) => state.getReleaseLogsInput = {},
    /**
     * Establece el evento actualmente seleccionado por el usuario
     *
     * @param state
     * @param log
     */
    [MutationTypes.SetCurrentLog]: (state, log: ReleaseLog) => state.currentLog = log,

    /**
     * Establece la lista de conexiones SQL registradas en la UTSL
     *
     * @param state
     * @param connections
     */
    [MutationTypes.SetSQLConnections]: (state, connections: SQLConnectionConfig[]) => {
        state.sqlConnections = connections
    },

    /**
     * Establece la conexión actualmente seleccionada por el usuario
     *
     * @param state
     * @param connections
     */
    [MutationTypes.SetCurrentSQLConnection]: (state, connection: SQLConnectionConfig) => {
        state.currentSQLConnection = connection
    },

    /**
     *  Establece una asignacion de distribución a un grupo de usuarios
     * @param state
     * @param payload
     */
    [MutationTypes.SetGroupReleaseAssignation]: (state, assignation: GroupReleaseAssignation) => {
        state.groupReleaseAssignation = assignation
    },
}

export default mutations
